import { matchPath } from 'react-router-dom'

import type { BoundWindowDescriptor, WindowDescriptor, WindowMatch, WindowParams } from './types'

/** Адрес окна без объявленных путей — корень приложения. */
export const ROOT_PATH = '/'

/**
 * Реестр окон нового клиента: один на приложение, собирается composition из
 * дескрипторов всех пакетов. Маршруты, меню, шапка и вкладки читают его, а не
 * ведут свои списки (R11, карточка 252).
 */
export interface WindowRegistry<R> {
  readonly all: readonly BoundWindowDescriptor<R>[]
  ofKind(kind: string): BoundWindowDescriptor<R> | undefined
  /** Окно на адресе (путь без `?`). `undefined` — адрес окном не обслуживается. */
  match(pathname: string): WindowMatch<R> | undefined
  identityOf(pathname: string): string | undefined
  /** Адрес окна: основной путь с подставленными параметрами. */
  pathOf(kind: string, params?: WindowParams): string
  /** Ссылка на окно в стопке модальных (`?w=`): `kind:знач1/знач2`. */
  refOf(kind: string, params?: WindowParams): string
  /** Разобрать ссылку из `?w=`; неизвестное окно или неполные параметры — `undefined`. */
  parseRef(ref: string): WindowMatch<R> | undefined
  /**
   * Окна меню группы в порядке работы. Вход раздела без содержимого — без
   * своей вкладки и без вкладок ресурсных окон — в меню не попадает: пункт,
   * который ничего не откроет, хуже его отсутствия.
   */
  menu(group: 'work' | 'settings'): readonly BoundWindowDescriptor<R>[]
  /** Раздел меню по имени (UI-SECTIONS). */
  section(id: string): WindowSection<R> | undefined
  /** Раздел, к которому относится окно; `undefined` — окно вне разделов. */
  sectionOf(kind: string): string | undefined
}

/** Раздел меню (UI-SECTIONS): вход и окна-вкладки пакетов видов в порядке реестра. */
export interface WindowSection<R> {
  id: string
  home: BoundWindowDescriptor<R>
  /** Подпись собственной вкладки входа («Общий каталог»); `null` — своей вкладки у входа нет. */
  homeTab: string | null
  /** Вход начинается с выбора источника плитками, а не открывает первую вкладку сам. */
  pickResource: boolean
  /** Ресурсные окна, дающие вкладки раздела (у каждого `section.tab`). */
  tabs: readonly BoundWindowDescriptor<R>[]
  /** Общие окна — собственные вкладки раздела без ресурса (у каждого `section.ownTab`), по порядку. */
  ownTabs: readonly BoundWindowDescriptor<R>[]
}

const SECTION_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

const PARAM = /:([A-Za-z][A-Za-z0-9]*)/g

function paramNames(path: string): string[] {
  return [...path.matchAll(PARAM)].map((match) => match[1] ?? '')
}

/** Параметры основного пути окна в его порядке — по ним строится identity. */
/**
 * `matchPath` раскодирует только «%2F»: адрес из роутера закодирован, и без
 * этого rid «база А» из адреса и из `?w=` дали бы два разных окна.
 */
function decodeSegment(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function primaryParams<R>(descriptor: BoundWindowDescriptor<R>): string[] {
  return paramNames(descriptor.paths[0] ?? '')
}

function identityOf<R>(descriptor: BoundWindowDescriptor<R>, params: WindowParams): string {
  // Значения экранируются: ключ с «/» не должен склеивать разные сущности.
  const values = primaryParams(descriptor)
    .map((name) => params[name])
    .filter((value): value is string => value !== undefined && value !== '')
    .map(encodeURIComponent)
  return values.length === 0 ? descriptor.kind : `${descriptor.kind}:${values.join('/')}`
}

/**
 * Привязать дескрипторы пакета к RootState: `capture` получает срез модуля.
 * Пакет так и не видит RootState (D-051).
 */
export function bindWindows<R, S>(
  moduleId: string,
  selectModule: (root: R) => S,
  descriptors: readonly WindowDescriptor<S>[],
): BoundWindowDescriptor<R>[] {
  return descriptors.map((descriptor) => {
    const { capture, ...rest } = descriptor
    return {
      ...rest,
      moduleId,
      ...(capture !== undefined
        ? { capture: (root: R, params: WindowParams) => capture(selectModule(root), params) }
        : {}),
    }
  })
}

/** Проверка дескрипторов: ошибка роняет сборку приложения и тест composition. */
function validate<R>(descriptors: readonly BoundWindowDescriptor<R>[]): void {
  const kinds = new Set<string>()
  const paths = new Map<string, string>()
  for (const descriptor of descriptors) {
    if (!descriptor.kind.startsWith(`${descriptor.moduleId}.`)) {
      throw new Error(`Окно «${descriptor.kind}» должно называться «${descriptor.moduleId}.<окно>»`)
    }
    if (descriptor.kind.includes(':') || descriptor.kind.includes('/')) {
      throw new Error(`Имя окна «${descriptor.kind}» не может содержать «:» и «/»`)
    }
    if (kinds.has(descriptor.kind)) throw new Error(`Окно «${descriptor.kind}» объявлено дважды`)
    kinds.add(descriptor.kind)
    if (descriptor.paths.length === 0) throw new Error(`У окна «${descriptor.kind}» нет адреса`)
    for (const path of descriptor.paths) {
      if (!path.startsWith('/')) throw new Error(`Адрес окна «${descriptor.kind}» должен начинаться с /: ${path}`)
      const owner = paths.get(path)
      if (owner !== undefined) throw new Error(`Адрес ${path} занят окнами «${owner}» и «${descriptor.kind}»`)
      paths.set(path, descriptor.kind)
      const names = paramNames(path)
      if (descriptor.scope === 'resource' && !names.includes('rid')) {
        throw new Error(`Ресурсное окно «${descriptor.kind}»: адрес ${path} без :rid (D-064)`)
      }
      if (descriptor.scope === 'global' && names.includes('rid')) {
        throw new Error(`Общее окно «${descriptor.kind}» не может нести :rid — объявите scope: 'resource'`)
      }
    }
  }
  validateSections(descriptors)
}

/**
 * Разделы (UI-SECTIONS): у раздела не больше одного входа, вход — общее окно с
 * местом в меню, вкладка — ресурсное окно с видом и правом. Что у каждого
 * упомянутого раздела вход ЕСТЬ, проверяет оболочка приложения
 * (`createWindowShell`): реестр одного пакета входов Platform не содержит.
 */
function validateSections<R>(descriptors: readonly BoundWindowDescriptor<R>[]): void {
  const homes = new Map<string, string>()
  for (const descriptor of descriptors) {
    const section = descriptor.section
    if (section === undefined) continue
    const where = `Окно «${descriptor.kind}», раздел «${section.id}»`
    if (!SECTION_ID.test(section.id)) throw new Error(`${where}: имя раздела — латиница, цифры и «-»`)
    if (section.home !== undefined) {
      if (section.tab !== undefined) throw new Error(`${where}: вход раздела не может быть вкладкой ресурса`)
      if (descriptor.scope !== 'global') throw new Error(`${where}: вход раздела — общее окно, без :rid`)
      if (descriptor.menu === undefined) throw new Error(`${where}: у входа раздела нет места в меню`)
      if (section.home.tab !== undefined && section.home.tab.trim() === '') throw new Error(`${where}: пустая подпись вкладки входа`)
      const owner = homes.get(section.id)
      if (owner !== undefined) throw new Error(`Раздел «${section.id}»: два входа — «${owner}» и «${descriptor.kind}»`)
      homes.set(section.id, descriptor.kind)
    }
    if (section.ownTab !== undefined) {
      if (section.home !== undefined || section.tab !== undefined) throw new Error(`${where}: собственная вкладка — не вход и не вкладка ресурса`)
      if (descriptor.scope !== 'global') throw new Error(`${where}: собственная вкладка раздела — общее окно, без :rid`)
      if (section.ownTab.label.trim() === '') throw new Error(`${where}: пустая подпись собственной вкладки`)
    }
    if (section.tab !== undefined) {
      if (descriptor.scope !== 'resource') throw new Error(`${where}: вкладка раздела — ресурсное окно с :rid`)
      const { resourceKind, capability, label } = section.tab
      if (resourceKind.trim() === '' || capability.trim() === '' || label.trim() === '') {
        throw new Error(`${where}: у вкладки нужны вид ресурса, право и подпись`)
      }
    }
  }
}

export function createWindowRegistry<R>(descriptors: readonly BoundWindowDescriptor<R>[]): WindowRegistry<R> {
  validate(descriptors)
  const byKind = new Map(descriptors.map((descriptor) => [descriptor.kind, descriptor]))
  const sections = new Map<string, WindowSection<R>>()
  for (const home of descriptors) {
    if (home.section?.home === undefined) continue
    const id = home.section.id
    sections.set(id, {
      id,
      home,
      homeTab: home.section.home.tab ?? null,
      pickResource: home.section.home.pickResource === true,
      // Сортировка устойчива: окна без `order` остаются в порядке реестра, после упорядоченных.
      tabs: descriptors
        .filter((descriptor) => descriptor.section?.id === id && descriptor.section.tab !== undefined)
        .sort(
          (left, right) =>
            (left.section?.tab?.order ?? Number.MAX_SAFE_INTEGER) - (right.section?.tab?.order ?? Number.MAX_SAFE_INTEGER),
        ),
      ownTabs: descriptors
        .filter((descriptor) => descriptor.section?.id === id && descriptor.section.ownTab !== undefined)
        .sort(
          (left, right) =>
            (left.section?.ownTab?.order ?? Number.MAX_SAFE_INTEGER) - (right.section?.ownTab?.order ?? Number.MAX_SAFE_INTEGER),
        ),
    })
  }
  /** Вход раздела без содержимого в меню не показывается. */
  const isEmptySectionHome = (descriptor: BoundWindowDescriptor<R>) => {
    const section = descriptor.section?.home === undefined ? undefined : sections.get(descriptor.section.id)
    return section !== undefined && section.homeTab === null && section.tabs.length === 0 && section.ownTabs.length === 0
  }

  function match(pathname: string): WindowMatch<R> | undefined {
    for (const descriptor of descriptors) {
      for (const path of descriptor.paths) {
        const found = matchPath({ path, end: true }, pathname)
        if (found === null) continue
        const params: Record<string, string> = {}
        for (const [name, value] of Object.entries(found.params)) {
          if (name !== '*' && value !== undefined && value !== '') params[name] = decodeSegment(value)
        }
        if (descriptor.scope === 'resource' && (params.rid === undefined || params.rid.trim() === '')) continue
        return { descriptor, params, identity: identityOf(descriptor, params) }
      }
    }
    return undefined
  }

  function pathOf(kind: string, params: WindowParams = {}): string {
    const descriptor = byKind.get(kind)
    if (descriptor === undefined) throw new Error(`Окно «${kind}» не объявлено в реестре`)
    const pattern = descriptor.paths[0] ?? ROOT_PATH
    return pattern.replace(PARAM, (_whole, name: string) => {
      const value = params[name]
      if (value === undefined || value === '') throw new Error(`Окну «${kind}» нужен параметр ${name}`)
      return encodeURIComponent(value)
    })
  }

  function refOf(kind: string, params: WindowParams = {}): string {
    const descriptor = byKind.get(kind)
    if (descriptor === undefined) throw new Error(`Окно «${kind}» не объявлено в реестре`)
    const values = primaryParams(descriptor).map((name) => {
      const value = params[name]
      if (value === undefined || value === '') throw new Error(`Окну «${kind}» нужен параметр ${name}`)
      return encodeURIComponent(value)
    })
    return values.length === 0 ? kind : `${kind}:${values.join('/')}`
  }

  function parseRef(ref: string): WindowMatch<R> | undefined {
    const separator = ref.indexOf(':')
    const kind = separator < 0 ? ref : ref.slice(0, separator)
    const descriptor = byKind.get(kind)
    if (descriptor === undefined) return undefined
    const names = primaryParams(descriptor)
    const raw = separator < 0 ? [] : ref.slice(separator + 1).split('/')
    if (raw.length !== names.length) return undefined
    const params: Record<string, string> = {}
    try {
      names.forEach((name, index) => {
        params[name] = decodeURIComponent(raw[index] ?? '')
      })
    } catch {
      return undefined
    }
    if (Object.values(params).some((value) => value === '')) return undefined
    return { descriptor, params, identity: identityOf(descriptor, params) }
  }

  return {
    all: descriptors,
    ofKind: (kind) => byKind.get(kind),
    match,
    identityOf: (pathname) => match(pathname)?.identity,
    pathOf,
    refOf,
    parseRef,
    menu: (group) =>
      descriptors
        .filter((descriptor) => descriptor.menu?.group === group && !isEmptySectionHome(descriptor))
        .sort((left, right) => (left.menu?.order ?? 0) - (right.menu?.order ?? 0)),
    section: (id) => sections.get(id),
    sectionOf: (kind) => byKind.get(kind)?.section?.id,
  }
}

/** Заголовок окна: «чем занято» или имя из меню. */
export function windowTitle<R>(match: Pick<WindowMatch<R>, 'descriptor' | 'params'>): string {
  return match.descriptor.title?.(match.params) ?? match.descriptor.label
}
